import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { BULK_SELECTION_INITIAL_STATE } from 'src/app/Orders/order.const';
import BulkActionBar from 'src/components/BulkActionBar';
import CardCmp from 'src/components/CardCmp';
import CheckBox from 'src/components/CheckBox';
import DraggableLayout from 'src/components/DraggableLayout';
import TableCmp from 'src/components/TableCmp';
import TableTabButton from 'src/components/TableTabButton';
import Toggle from 'src/components/Toggle/Toggle';
import { currentUser as currentUserSelector } from 'src/redux/auth.slice';
import { orderDetailsData, orderDetailsLoading } from 'src/redux/orders.slice';

import DraggableColumnsButton from './LegOverview/DraggableColumnsButton';
import { ROUTE_OVERVIEW_SHOW_BY_STOP_STORAGE_KEY } from './LegOverview/legOverview.const';
import { buildCollapsedRouteRows, buildRouteRows, isCoveredLeg } from './LegOverview/legOverview.helpers';
import LegOverviewLoaderRow from './LegOverview/LegOverviewLoaderRow';
import LegOverviewRow from './LegOverview/LegOverviewRow';
import RouteOverviewDraggableCell from './LegOverview/RouteOverviewDraggableCell';
import useRouteOverviewColumns from './LegOverview/useRouteOverviewColumns';

/** Per-viewer preference; storage can be unavailable (private window, blocked site data) — fall back to ON. */
const readShowByStop = (): boolean => {
    try {
        return localStorage.getItem(ROUTE_OVERVIEW_SHOW_BY_STOP_STORAGE_KEY) !== 'false';
    } catch {
        return true;
    }
};

/**
 * Route Overview — the order's legs as WALHQ shows them (same columns, "Show by Stop" nesting, column
 * manager). Reads the presented legs from the order payload: with Display Order Legs off the portal
 * backend has already merged WAL-internal junctions, so no stop rows exist and the toggle hides itself.
 * Also mounted on the public share page, where there is no logged-in user and nothing can be saved.
 */
const LegOverviewSection = () => {
    const { t } = useTranslation();
    const translate = t as (key: string, options?: Record<string, any>) => string;
    const order = useSelector(orderDetailsData);
    const isLoading = useSelector(orderDetailsLoading);
    const loggedInUser = useSelector(currentUserSelector);
    const canManageColumns = !!loggedInUser;

    const [bulkSelection, setBulkSelection] = useState<{ isOpen: boolean; data: number[] }>(BULK_SELECTION_INITIAL_STATE);
    const [showAllSelected, setShowAllSelected] = useState(false);
    const [isShowByStop, setIsShowByStop] = useState<boolean>(readShowByStop);

    useEffect(() => {
        try {
            localStorage.setItem(ROUTE_OVERVIEW_SHOW_BY_STOP_STORAGE_KEY, String(isShowByStop));
        } catch {
            // per-viewer convenience only — nothing to recover
        }
    }, [isShowByStop]);

    const selectedIds = bulkSelection.data;

    // Single presented leg (direct order, or fully consolidated by Display Order Legs OFF): the row IS
    // the whole shipment, so it carries the MASTER id — the leg's own id (#…-2472 on order #…-2471)
    // reads as a different order. Display-only; the row keeps its real leg id for selection.
    const legs = useMemo(() => {
        const rawLegs = (order?.orderLegs ?? []).filter((leg: any) => !leg?.isDeleted);

        if (rawLegs.length === 1 && order?.orderId) {
            return [{ ...rawLegs[0], legId: order.orderId }];
        }

        return rawLegs;
    }, [order?.orderLegs, order?.orderId]);

    // Offered only when a stop row exists — a covered leg IS the stop row the toggle hides. Without one
    // both states render the same table, so the control would do nothing.
    const hasStopRows = useMemo(() => legs.some((leg: any) => isCoveredLeg(leg)), [legs]);
    const visibleLegs = useMemo(() => (isShowByStop || !hasStopRows ? legs : buildCollapsedRouteRows(legs)), [legs, isShowByStop, hasStopRows]);
    // Only leg rows carry a checkbox — stop rows belong to their run.
    const selectableIds = useMemo(() => visibleLegs.filter((leg: any) => !isCoveredLeg(leg)).map((leg: any) => leg.id), [visibleLegs]);

    const setSelectedIds = useCallback((ids: number[]) => {
        if (ids.length === 0) setShowAllSelected(false);
        setBulkSelection({ isOpen: ids.length > 0, data: ids });
    }, []);

    const handleToggleSelectRow = useCallback((id: number) => {
        setBulkSelection(({ data }) => {
            const next = data.includes(id) ? data.filter((i) => i !== id) : [...data, id];
            if (next.length === 0) setShowAllSelected(false);

            return { isOpen: next.length > 0, data: next };
        });
    }, []);

    const displayedData = useMemo(() => buildRouteRows(showAllSelected ? visibleLegs.filter((leg: any) => selectedIds.includes(leg.id)) : visibleLegs), [showAllSelected, visibleLegs, selectedIds]);

    const pinnedFirstColumns = useMemo(
        () => [
            {
                id: 'checkbox',
                name: (
                    <CheckBox
                        id="selectAllLegs"
                        checked={selectableIds.length > 0 && selectableIds.every((id: number) => selectedIds.includes(id))}
                        onChangeFunc={() => setSelectedIds(selectableIds.every((id: number) => selectedIds.includes(id)) ? [] : selectableIds)}
                        name="selectAllLegs"
                        value="selectAllLegs"
                        disabled={isLoading}
                    />
                ),
                sortable: false,
                visible: true,
                rowClassName: '!pr-2 w-[40px] min-w-[40px] max-w-[40px] sticky left-0 bg-inherit z-[1]',
            },
        ],
        [selectableIds, selectedIds, setSelectedIds, isLoading]
    );

    const previewContext = useMemo(() => ({ t: translate, legs }), [translate, legs]);

    // Card title is WALHQ's tab pill with the row count inside (a single, always-active tab).
    const title = t('Orders.Route_Overview_Table.Title');
    const titleTabs = useMemo(() => [{ id: title, value: title, name: title, count: visibleLegs.length }], [title, visibleLegs.length]);

    const { headCells, isEditing, columnsButtonProps, sortableData, onColumnChange } = useRouteOverviewColumns({
        rows: displayedData,
        pinnedFirstColumns,
        previewContext,
        isDisabled: isLoading || !canManageColumns,
    });

    return (
        <CardCmp
            headerTitle={
                <TableTabButton
                    tabArray={titleTabs}
                    isActive={title}
                    handleOnClick={() => {}}
                    className={`rounded-md px-3 py-1.5 ${isLoading ? 'custom-loading' : ''}`}
                    tabTextClass="!max-w-[160px] truncate"
                />
            }
            headerRightSideContent={
                <div className="flex items-center gap-x-2">
                    {!isEditing && hasStopRows && (
                        <Toggle
                            id="route-overview-show-by-stop"
                            name="showByStop"
                            label={t('Orders.Route_Overview_Table.Show_By_Stop')}
                            labelPosition="left"
                            isChecked={isShowByStop}
                            onChange={(e: any) => setIsShowByStop(!!e.target.checked)}
                            isDisabled={isLoading}
                            labelClassName={isLoading ? 'custom-loading rounded-md' : ''}
                        />
                    )}
                    {canManageColumns && <DraggableColumnsButton {...columnsButtonProps} />}
                </div>
            }
            cardClassName="lg:min-h-fit !rounded-b-lg overflow-hidden"
            headerParentClassName="!rounded-t-lg"
            isCardContentWithoutSpacing={true}
        >
            <div className={`draggable-layout-view ${isEditing ? 'slide-in' : 'slide-out'}`}>
                <DraggableLayout sortableData={sortableData} renderCellContent={RouteOverviewDraggableCell} onColumnChange={onColumnChange} />
            </div>
            <div className={`w-full table-box-container relative draggable-layout-view ${isEditing ? 'slide-out pointer-events-none' : 'slide-in'}`}>
                {bulkSelection.isOpen && (
                    <BulkActionBar
                        selectedRows={selectedIds}
                        setSelectedRows={setSelectedIds}
                        allIds={selectableIds}
                        showOnlySelected={showAllSelected}
                        onShowOnlySelectedChange={setShowAllSelected}
                    />
                )}
                <div className="overflow-x-auto custom-scrollbar scrollbar-hide">
                    <TableCmp
                        tableRowHeight="xs"
                        headCells={headCells}
                        tableDataArr={isLoading ? [] : displayedData}
                        TableRowCmp={LegOverviewRow}
                        TableLoaderRowCmp={LegOverviewLoaderRow}
                        isTableDataLoading={isLoading || !order}
                        tableBodyRowClass="last:border-b-0 bg-white"
                        numberOfSkeletonRows={3}
                        isFirstColumnSticky
                        isBulkBarOpen={bulkSelection.isOpen}
                        tableRowCmpProps={{ legs, headCells, selectedIds, onToggleSelect: handleToggleSelectRow }}
                    />
                </div>
            </div>
        </CardCmp>
    );
};

export default LegOverviewSection;
